import { Close, VerifiedUser } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import WarrantiesTable from 'lib/common/Tables/WarrantiesTable';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { Warranty } from 'lib/types/models';
import {
  BaseOperation,
  OPERATION_TYPES,
} from 'pages/BusinessProfile/Profile/components/Contracts/Components/CreatePortfolio/Components/Inputs/OperationAssociation/operations';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

interface OperationsDialogProps {
  open: boolean;
  onClose: () => void;
  operationType: string;
  onSelectOperation: (operation: BaseOperation) => void;
  businessIdentifier: string | undefined;
}

const OperationsDialog: React.FC<OperationsDialogProps> = ({
  open,
  onClose,
  operationType,
  onSelectOperation,
  businessIdentifier,
}) => {
  const dispatch = useDispatch();
  const [operations, setOperations] = useState<BaseOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(BASE_PAGINATION);

  const config = OPERATION_TYPES[operationType];

  const fetchOperations = async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<void> => {
    const { page = pagination.page, pageSize = pagination.pageSize } =
      params ?? {};
    if (!config) return;

    try {
      setLoading(true);
      const response = await config.fetchData(
        dispatch,
        page,
        pageSize,
        businessIdentifier,
      );
      setOperations(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOperation = (id: number) => {
    const operation = operations.find((o) => o.id === id);
    if (operation) {
      onSelectOperation(operation);
      onClose();
    }
  };

  useEffect(() => {
    if (open && config) {
      void fetchOperations({ page: 1, pageSize: 10 });
    }
  }, [open, operationType]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Title
            title="Operaciones"
            icon={<VerifiedUser color="error" />}
            color="error"
          />
        </DialogTitle>
        <DialogContent>
          <p>Tipo de operación no soportado</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title
          title={`Operaciones - ${config.label}`}
          icon={<VerifiedUser color="success" />}
          color="success"
        />
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <WarrantiesTable
          warranties={operations as Warranty[]}
          setWarranties={setOperations}
          handleOpenWarrantyDetail={handleSelectOperation}
          pagination={pagination}
          setPagination={setPagination}
          loading={loading}
          fetchWarranties={fetchOperations}
          setIsLoading={setLoading}
          showBusiness={false}
        />
      </DialogContent>
    </Dialog>
  );
};

export default OperationsDialog;
